import { useState } from "react"

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Bookmark, Plus, Trash2 } from "@/components/icons"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { api } from "@/lib/api-client"

type Bookmark = {
  id: string
  name: string
  state: Record<string, unknown>
  created_at: string
}

type BookmarkMenuProps = {
  dashboardId: string
  /** Snapshot of the live dashboard state at save time. Returned by
   *  the page-level `dashboardStateGetterRef` that already exists. */
  getState: () => Record<string, unknown>
  /** Apply a previously saved snapshot back to the live store. */
  onApply: (state: Record<string, unknown>) => void
}

/**
 * Save / restore / delete personal filter views for a dashboard —
 * Power BI's "Bookmarks" pane, scoped to the calling user. Backend
 * stores the snapshots on the user's settings doc so each user keeps
 * their own list even on a shared dashboard.
 */
export function BookmarkMenu({ dashboardId, getState, onApply }: BookmarkMenuProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [showSaveDialog, setShowSaveDialog] = useState(false)
  const [pendingName, setPendingName] = useState("")
  const [bookmarkToDelete, setBookmarkToDelete] = useState<Bookmark | null>(null)

  const queryKey = ["dashboard-bookmarks", dashboardId] as const
  const { data: bookmarks = [], isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      const resp = await api.get<unknown, { bookmarks: Bookmark[] }>(
        `/dashboards/${dashboardId}/bookmarks`
      )
      return resp.bookmarks
    },
    enabled: !!dashboardId,
    staleTime: 1000 * 30,
  })

  const saveMutation = useMutation({
    mutationFn: ({ name }: { name: string }) =>
      api.post<unknown, { bookmark: Bookmark }>(`/dashboards/${dashboardId}/bookmarks`, {
        name,
        state: getState(),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey })
      toast.success(t("dash.bookmarks.save_success", { defaultValue: "View saved" }))
      setShowSaveDialog(false)
      setPendingName("")
    },
    onError: (err: Error) => {
      toast.error(t("dash.bookmarks.save_error", { defaultValue: "Failed to save view" }), {
        description: err.message,
      })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (bookmarkId: string) =>
      api.delete<unknown, { success: boolean }>(
        `/dashboards/${dashboardId}/bookmarks/${bookmarkId}`
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey })
      setBookmarkToDelete(null)
    },
    onError: (err: Error) => {
      toast.error(t("dash.bookmarks.delete_error", { defaultValue: "Failed to delete" }), {
        description: err.message,
      })
    },
  })

  return (
    <>
      <DropdownMenu>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button
                aria-label={t("dash.bookmarks.aria", { defaultValue: "Saved views" })}
                className="size-8 p-0"
                data-testid="bookmark-menu-trigger"
                size="icon"
                variant="ghost"
              >
                <Bookmark className="size-4" />
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent>
            {t("dash.bookmarks.tooltip", { defaultValue: "Saved views" })}
          </TooltipContent>
        </Tooltip>
        <DropdownMenuContent align="end" className="w-72">
          <DropdownMenuLabel>
            {t("dash.bookmarks.heading", { defaultValue: "Saved views" })}
          </DropdownMenuLabel>
          <DropdownMenuItem onSelect={() => setShowSaveDialog(true)}>
            <Plus className="mr-2 size-4" />
            {t("dash.bookmarks.save_current", { defaultValue: "Save current view…" })}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          {isLoading && (
            <div className="text-muted-foreground px-2 py-1.5 text-xs">
              {t("common.loading", { defaultValue: "Loading…" })}
            </div>
          )}
          {!isLoading && bookmarks.length === 0 && (
            <div className="text-muted-foreground px-2 py-1.5 text-xs">
              {t("dash.bookmarks.empty", {
                defaultValue: "No saved views yet — save the current filters to reuse them.",
              })}
            </div>
          )}
          {bookmarks.map((bookmark) => (
            <DropdownMenuItem
              key={bookmark.id}
              className="group/bookmark flex items-start justify-between gap-2"
              data-testid={`bookmark-item-${bookmark.id}`}
              onSelect={() => onApply(bookmark.state)}
            >
              <span className="truncate">{bookmark.name}</span>
              <button
                aria-label={t("dash.bookmarks.delete", { defaultValue: "Delete" })}
                className="text-muted-foreground hover:text-destructive opacity-0 transition group-hover/bookmark:opacity-100"
                type="button"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  setBookmarkToDelete(bookmark)
                }}
              >
                <Trash2 className="size-3.5" />
              </button>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t("dash.bookmarks.save_dialog_title", { defaultValue: "Save current view" })}
            </DialogTitle>
            <DialogDescription>
              {t("dash.bookmarks.save_dialog_description", {
                defaultValue:
                  "Stores your active filters so you can come back to this exact slice later.",
              })}
            </DialogDescription>
          </DialogHeader>
          <Input
            autoFocus
            data-testid="bookmark-name-input"
            value={pendingName}
            placeholder={t("dash.bookmarks.name_placeholder", {
              defaultValue: "e.g. Q3 — Stockholm only",
            })}
            onChange={(e) => setPendingName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && pendingName.trim()) {
                e.preventDefault()
                saveMutation.mutate({ name: pendingName.trim() })
              }
            }}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveDialog(false)}>
              {t("common.cancel", { defaultValue: "Cancel" })}
            </Button>
            <Button
              data-testid="bookmark-save-button"
              disabled={!pendingName.trim() || saveMutation.isPending}
              onClick={() => saveMutation.mutate({ name: pendingName.trim() })}
            >
              {t("common.save", { defaultValue: "Save" })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={bookmarkToDelete !== null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setBookmarkToDelete(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("dash.bookmarks.delete_title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("dash.bookmarks.delete_description", { name: bookmarkToDelete?.name ?? "" })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteMutation.isPending}
              onClick={() => {
                if (bookmarkToDelete) deleteMutation.mutate(bookmarkToDelete.id)
              }}
            >
              {t("dash.bookmarks.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
